<script>
import dayjs from "dayjs";
import fileDownload from 'js-file-download';
import { mapState } from "vuex";
import Multiselect from "vue-multiselect";

import Layout from "../../layouts/main";
import appConfig from "@/app.config";
import PageHeader from "@/components/page-header";

export default {
  page: {
    title: "Packages",
    meta: [{ name: "description", content: appConfig.description }],
  },
  async mounted() {
    this.apiGet(`/mastheads`, { exclude: 'logoImage,printSizes' }, true).then((data) => {
      this.mastheadOptions = data.data
    }).catch((error) => {
      this.$store.dispatch('layout/addPageError', error);
    })
  },
  components: {
    Layout,
    PageHeader,
    Multiselect
  },
  methods: {
    getFilterParams() {
      return {
        masthead: this.filters.masthead ? this.filters.masthead : null,
        packageType: this.filters.packageType ? this.filters.packageType : null,
      }
    },
    async fetchData(ctx) {
      return this.apiGet(`/packages`, { include: 'deleted', sort: ctx.sortBy, order: ctx.sortDesc ? 'desc' : 'asc', limit: ctx.perPage, page: ctx.currentPage, searchString: ctx.filter, ...this.getFilterParams() }).then((data) => {
        this.totalRows = data ? data.meta.cursor.count : 0;
        return data.data;
      }).catch((error) => {
        this.$store.dispatch('layout/addPageError', error);
        return [];
      })
    },
    lastModified(date) {
      return dayjs(date).format('DD/MM/YYYY')
    },
    getReport() {
      this.apiGetFile(`/packages/report`, { }, 'application/csv').then((data) => {
        fileDownload(data, `advantage-packages-${dayjs().format('DD/MM/YYYY')}.csv`);
      }).catch((error) => {
        this.$store.dispatch('layout/addPageError', error);
      })
    },
    importCsv() {
      const file = this.$refs.fileUpload.files[0];
      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      this.apiPostFile('/packages/import', formData).then(() => {
        this.importHasError = false;
        this.importErrors = null;

        this.importSuccess = true,

        this.$refs.fileUpload.value = null;
        
        this.$refs.table.refresh();
      }).catch((error) => {
        this.importHasError = true;
        this.importErrors = error.response ? error.response.data.error.message : null;
        
        this.importSuccess = false,

        this.$refs.fileUpload.value = null;
      });
    },
    handleMastheadSelect({ id }) {
      this.filters.masthead = id;

      this.$refs.table.refresh();
    },
    handlePackageTypeSelect(packageType) {
      this.filters.packageType = packageType;

      this.$refs.table.refresh();
    },
    clearFilters() {
      this.filters = {
        masthead: '',
        packageType: '',
      };

      this.$refs.table.refresh();
    },
  },
  computed: {
    ...mapState('auth', ['currentUser']),
    mastheadValue() {
      return this.mastheadOptions ? this.mastheadOptions.find((masthead) => masthead.id === this.filters.masthead) : null;
    },
    packageTypeValue() {
      return this.packageTypeOptions ? this.packageTypeOptions.find((packageType) => packageType === this.filters.packageType) : null;
    },
  },
  data() {
    return {
      title: "Packages",
      items: [
        {
          text: "Packages",
          href: "/packages",
        },
        {
          text: "Index",
          active: true,
        },
      ],
      mastheadOptions: [],
      packageTypeOptions: [
        'Digital',
        'Print & digital',
        'Video',
      ],
      filters: {
        masthead: '',
        packageType: '',
      },
      totalRows: 1,
      currentPage: 1,
      perPage: 20,
      pageOptions: [20, 50, 100, 200],
      filter: null,
      filterOn: [],
      sortBy: "updatedAt",
      sortDesc: true,
      fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "type", label: "Type", sortable: true },
        { key: "masthead.name", label: "Masthead", sortable: true },
        { key: "objective", label: "Objective", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "updatedAt", label: "Last Modified", sortable: true },
        { key: "action", label: "", sortable: false }
      ],
      importHasError: null,
      importErrors: null,
      importSuccess: false,
    };
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row mb-3" v-if="this.currentUser.role === 'ROLE_SUPER_ADMIN'">
      <div class="col-12 text-right">
        <router-link to="/packages/create">
          <b-button variant="primary">
            <i class="bx bxs-plus-circle font-size-16 align-middle mr-2"></i> Add New
          </b-button>
        </router-link>
        <b-button variant="outline-primary" class="ml-2" @click="getReport">
          Export as CSV
        </b-button>
      </div>
    </div>
    
    <div class="row mb-3" v-if="this.currentUser.role === 'ROLE_SUPER_ADMIN'">
      <div class="col-12 text-right">
        <b-alert v-model="importHasError" variant="danger" dismissible>Error - We are unable to import Packages due to: {{ importErrors }}</b-alert>
        <b-alert v-model="importSuccess" variant="success" dismissible>Packages were imported successfully</b-alert>
        <div class="btn-group" role="group" aria-label="Basic example">
          <input class="btn btn-upload" type="file" ref="fileUpload" accept=".csv">
          <b-button variant="outline-primary" @click="importCsv()">
            Import CSV
          </b-button>
        </div>
      </div>
    </div>

    <div class="row mb-1 justify-content-end">
      <div class="col-12 col-md-2">
        <multiselect
          :options="mastheadOptions"
          :value="mastheadValue"
          @select="handleMastheadSelect"
          class="mb-2"
          deselect-label=""
          label="name"
          name="masthead"
          placeholder="Filter by Masthead"
          select-label=""
          track-by="id"
        ></multiselect>
      </div>
      <div class="col-12 col-md-2">
        <multiselect 
          :options="packageTypeOptions"
          :value="packageTypeValue" 
          @select="handlePackageTypeSelect" 
          deselect-label=""
          placeholder="Filter by Package Type"
          select-label=""
        ></multiselect>
      </div>
      <div class="col-12 col-md-2 mb-2">
        <b-button class="w-100" @click="clearFilters" variant="primary">Clear Filters</b-button>
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_length" class="dataTables_length">
                  <label class="d-inline-flex align-items-center">
                    Show&nbsp;
                    <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>&nbsp;entries
                  </label>
                </div>
              </div>
              <!-- Search -->
              <div class="col-sm-12 col-md-6">
                <div id="tickets-table_filter" class="dataTables_filter text-md-right">
                  <label class="d-inline-flex align-items-center">
                    Search:
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Search..."
                      class="form-control form-control-sm ml-2"
                    ></b-form-input>
                  </label>
                </div>
              </div>
              <!-- End search -->
            </div>
            <!-- Table -->
            <div class="table-responsive mb-0">
              <b-table
                ref="table"
                :items="fetchData"
                :fields="fields"
                responsive="sm"
                :per-page="perPage"
                :current-page="currentPage"
                :sort-by.sync="sortBy"
                :sort-desc.sync="sortDesc"
                :filter="filter"
                :filter-included-fields="filterOn"
                no-local-sorting
              >
              <template v-slot:cell(updatedAt)="row">
                {{ lastModified(row.item.updatedAt) }}
              </template>
              <template v-slot:cell(status)="row">
                <span class="badge badge-pill font-size-12" :class="getBadgeClassByStatus(row.item.deletedAt ? 'Archived' : row.item.status)">
                  {{ row.item.deletedAt ? 'Archived' : row.item.status }}
                </span>
              </template>
              <template v-slot:cell(action)="row">
                <router-link :to="`/packages/${row.item.id}/edit`" class="d-flex align-items-center">{{ currentUser.role !== 'ROLE_SALES_DIRECTOR' ? 'Edit Package' : 'View Package' }} <i class="bx bx-chevron-right" /></router-link>
              </template>
              </b-table>
            </div>
            <div class="row">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <!-- pagination -->
                    <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>